import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Home, AlertTriangle, Terminal } from 'lucide-react';
import FoxLogo from '../components/FoxLogo';
import { Button } from '../components/ui/button';

// 🔥 DİL ENTEGRASYONU 🔥
import { useLanguage } from '../context/LanguageContext';

const NotFoundPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage(); // Dil fonksiyonunu çağır

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#020617] text-slate-200 p-6 text-center relative overflow-hidden font-sans selection:bg-red-500/30">
      
      {/* Background Texture & Grid */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-600/5 rounded-full blur-[100px] animate-pulse pointer-events-none"></div>

      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-10 max-w-2xl w-full"
      >
        
        {/* Animated Fox Container */}
        <div className="relative mb-10 flex justify-center">
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative p-8 bg-[#0f172a]/50 rounded-[3rem] border border-white/10 backdrop-blur-xl shadow-2xl shadow-orange-900/20"
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/10 to-transparent rounded-[3rem]"></div>
            <FoxLogo size={120} className="text-white drop-shadow-[0_0_30px_rgba(234,88,12,0.4)] relative z-10" />
            
            {/* Error Badge */}
            <div className="absolute -top-3 -right-3 bg-red-500 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1 shadow-lg border border-white/10">
                <AlertTriangle size={12} /> Error
            </div>
          </motion.div>
        </div>
        
        {/* Typography */}
        <div className="space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
             <h1 className="text-9xl md:text-[12rem] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-white via-slate-200 to-slate-800 opacity-90 select-none">
               404
             </h1>
             <div className="h-2 w-24 bg-orange-500 mx-auto rounded-full mt-4 mb-8 shadow-[0_0_15px_#ea580c]"></div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <h2 className="text-2xl md:text-3xl font-black tracking-tight uppercase italic text-white">
              {t('error_404_title') || 'System Signal Lost.'}
            </h2>
            <p className="text-slate-400 text-lg font-medium leading-relaxed max-w-md mx-auto">
              {t('error_404_desc') || 'The data block you are looking for may have been deleted or never existed. Check your coordinates, Captain.'}
            </p>
          </motion.div>
        </div>

        {/* Fake Terminal Log */}
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-10 mx-auto max-w-md bg-[#050505] rounded-xl border border-white/10 p-4 font-mono text-[10px] text-left text-slate-500 shadow-inner overflow-hidden"
        >
            <div className="flex items-center gap-2 mb-2 border-b border-white/5 pb-2">
                <Terminal size={12} className="text-orange-500" />
                <span className="uppercase tracking-widest text-slate-400">{t('terminal_log_viewer') || 'System_Log_Viewer'}</span>
            </div>
            <div className="space-y-1 opacity-70">
                <p>> {t('log_searching') || 'INITIATING_SEARCH_PROTOCOL...'}</p>
                <p>> {t('log_target') || 'TARGET:'} "{window.location.pathname}"</p>
                <p className="text-red-500">> {t('log_error') || 'ERROR: PATH_NOT_RESOLVED'}</p>
                <p>> {t('log_status') || 'STATUS: 404_NOT_FOUND'}</p>
                <p className="animate-pulse">> {t('log_waiting') || 'WAITING_FOR_USER_INPUT_'}</p>
            </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-10 flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Button 
            onClick={() => navigate(-1)}
            variant="outline"
            className="h-14 px-8 rounded-xl border-white/10 bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white font-bold transition-all uppercase tracking-widest text-xs"
          >
            <ArrowLeft size={16} className="mr-2" /> {t('btn_go_back') || 'Go Back'}
          </Button>
          
          <Button 
            onClick={() => navigate('/')}
            className="h-14 px-8 rounded-xl bg-orange-600 hover:bg-orange-500 text-white font-black uppercase tracking-widest text-xs shadow-xl shadow-orange-900/20 transition-all hover:scale-105 active:scale-95"
          >
            <Home size={16} className="mr-2" /> {t('btn_go_home') || 'Dashboard'}
          </Button>
        </motion.div>

      </motion.div>

      {/* Footer Branding */}
      <div className="absolute bottom-6 left-0 right-0 text-center">
          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-600 opacity-50">
            BüroFuchs Intelligence Systems v3.0
          </p>
      </div>
    </div>
  );
};

export default NotFoundPage;